import styles from './folderModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useUpdateFolderMutation
} from "../../api/testCaseTemplateFolderService";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  object,
  string
} from "yup";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { setUpdateModal } from "../../store/slices/mainSlice";
import { Folder } from "../../types/testCaseTemplateFolderTypes";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";

type FolderModalProps = {
  onClose: (isSave: boolean, idCreate?: number) => void;
  folder?: Folder
}

export const FolderModal = ({ onClose, folder }: FolderModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>()

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [createFolder, { data: createData, isSuccess: isSuccessCreate, isLoading: isLoadingCreate }] = useCreateFolderMutation();
  const [updateFolder, { isSuccess: isSuccessUpdate, isLoading: isLoadingUpdate }] = useUpdateFolderMutation();
  const [deleteFolder, { isSuccess: isSuccessDelete, isLoading: isLoadingDelete }] = useDeleteFolderMutation();

  useEffect(() => {
    if (isSuccessCreate && createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true, createData);
    }
  }, [isSuccessCreate]);

  useEffect(() => {
    if (isSuccessUpdate) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true);
    }
  }, [isSuccessUpdate]);

  useEffect(() => {
    if (isSuccessDelete) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onClose(true);
    }
  }, [isSuccessDelete]);


  const schema = object({
    name: string().required(t("common.notValid")).typeError(t("common.notValid")),
  });

  const { control, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: folder?.name || ""
    },
    mode: "onChange"
  });

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickSave = (): void => {
    handleSubmit((value) => {
      const newFolder: Folder = {
        name: value.name,
      };
      if (!!folder) {
        newFolder.id = folder.id;
        updateFolder(newFolder);
      } else {
        createFolder(newFolder);
      }
    })();
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!folder?.id) {
      deleteFolder(folder.id);
    }
    setShowDeleteMessage(false);
  }

  return (
    <>
      <ModalWindow onClose={onCancel}
                   width={380}
                   title={!!folder?.id ? t("testCaseTemplate.editFolder") : t("testCaseTemplate.addFolder")}
                   children={
                     <div className={styles.container}>
                       <div className={styles.inputContainer}>
                         <span className={styles.required}>{t("common.name")}</span>
                         <div className={styles.input}>
                           <Input control={control}
                                  name={"name"}
                                  maxLength={100}
                                  error={!!errors.name ? errors.name.message : ""}/>
                         </div>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!folder && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </div>
                   }
      />

      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("scenario.delete")}
                       description={t("scenario.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}